'use client';

import { useEffect, useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import Script from 'next/script';
import { deviceIDAtom } from '@/store';
import { getDeviceId, isInApp } from '@/libs/comeAppSDK';
import toast from 'react-hot-toast';

// 部分浏览器环境（Safari 隐私模式、跨域 iframe、隐私扩展等）会禁止访问 localStorage，
// 统一封装读写操作，避免 SecurityError 冒泡导致 deviceId 永远为 null。
function readStoredDeviceId(): string | null {
  try {
    return localStorage.getItem('deviceId');
  } catch {
    return null;
  }
}

function writeStoredDeviceId(id: string): void {
  try {
    localStorage.setItem('deviceId', id);
  } catch {
    // 写入失败时静默忽略，id 仅在当前 session 内存中有效
  }
}

export const useDeviceID = () => {
  return useAtomValue(deviceIDAtom);
};

export function DeviceIDProvider({ children }: { children: React.ReactNode; }) {
  const setDeviceID = useSetAtom(deviceIDAtom);

  // FingerprintJS 加载失败或运行异常时的兜底：用 UUID 确保 deviceId 始终非空，
  // 避免登录按钮永久禁用。仅在没有已存储 id 时才写入，不覆盖有效指纹。
  const setFallbackDeviceID = useCallback(() => {
    if (readStoredDeviceId()) return;
    if (typeof crypto === 'undefined' || !crypto.randomUUID) {
      toast.error('Device initialization failed. Please close and reopen the app.');
      return;
    }
    const fallbackID = crypto.randomUUID();
    writeStoredDeviceId(fallbackID);
    setDeviceID(fallbackID);
  }, [setDeviceID]);

  const getFingerprint = useCallback(async () => {
    if (isInApp()) {
      return;
    }

    try {
      // 脚本已加载，window.FingerprintJS 应当可用
      if (!window.FingerprintJS) {
        setFallbackDeviceID();
        return;
      }
      const fp = await window.FingerprintJS.load();
      const result = await fp.get();
      const newID = result.visitorId;

      writeStoredDeviceId(newID);
      setDeviceID(newID);
    } catch (error) {
      console.error('Failed to get fingerprint:', error);
      // FingerprintJS 运行异常，降级为 UUID 兜底
      setFallbackDeviceID();
    }
  }, [setDeviceID, setFallbackDeviceID]);

  // 挂载时优先从 localStorage 恢复已有 id
  useEffect(() => {
    const storedID = readStoredDeviceId();
    if (storedID) {
      setDeviceID(storedID);
    }
  }, [setDeviceID]);

  // apk内获取设备id逻辑
  const getAppDeviceId = useCallback(async () => {
    const storedID = readStoredDeviceId();
    if (storedID) {
      setDeviceID(storedID);
      return;
    }
    const deviceId = await getDeviceId();
    writeStoredDeviceId(deviceId);
    setDeviceID(deviceId);
  }, [setDeviceID]);
  useEffect(() => {
    if (isInApp()) {
      getAppDeviceId();
    }
  }, [getAppDeviceId]);

  return (
    <>
      <Script
        src="/scripts/fingerprintjs-v4.js"
        strategy="afterInteractive"
        onLoad={getFingerprint}
        onError={setFallbackDeviceID}
      />
      {children}
    </>
  );
}
